import {
    Component,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    ColDef,
    DetailGridInfo,
    GetDetailRowDataParams,
    GetRowIdParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    IServerSideDatasource,
    IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { Store } from '@ngrx/store';
import { first, take } from 'rxjs';

import { RouterService } from '@services/router.service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { ColumnOptionsService } from '@services/column-options.service';
import { DocumentDataService } from '@services/data/document-data.service';
import { DocumentService } from '@services/api/document/document.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { TableComponent } from '@grid-table/table.component';
import { TextFilterComponent } from '@grid-table/filter/text-filter/text-filter.component';
import { SelectFilterComponent } from '@grid-table/filter/select-filter/select-filter.component';
import { PriorityCellComponent } from '@grid-table/cell/priority-cell/priority-cell.component';
import { NoRowsOverlayComponent } from '@grid-table/overlay/no-rows-overlay/no-rows-overlay.component';
import { ClickableCellComponent } from '@shared/table/cell/clickable-cell/clickable-cell.compoent';
import { DateFilterComponent } from '@shared/table/filter/date-filter/date-filter.component';
import { GrayBoxCellComponent } from '@shared/table/cell/gray-box-cell/gray-box-cell.component';
import { RangeFilterComponent } from '@shared/table/filter/range-filter/range-filter.component';
import { CountStatusBarComponent } from './count-status-bar.component';
import { Workflow } from '@models/workflow/workflow';
import { CustomColumn } from '@models/workflow/custom-column';
import { DocumentFilterOptions, WorkflowDocument } from '@models/document';
import { Discrepancy, BulkRequestItem } from '@models/discrepancy';
import {
    BulkTakeDiscrepancyRequest,
    BulkActionRequest,
    DocumentViewQueryParameters,
} from '@models/discrepancy/bulk-request';
import { Range } from '@models/shared/range';
import { pickFromEnum } from '@helpers/index';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import {
    getFilterQuery,
    getPageIndex,
    getSortQuery,
    hideHiddenColumns,
    isFilterApplied,
    setFilterAndSorting,
} from '@helpers/table';
import { customColumnsToColDefs } from '@helpers/custom-column';
import { saveSummaryPanelData } from 'app/store/actions/document.actions';
import {
    DocumentColumn,
    DocumentDetailTab,
    DocumentNestedColumn,
    getAgeInWFValue,
    getDiscrepancyId,
    highlightSelectedDiscrepancy,
} from '@main/utils';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-document-view',
    templateUrl: './document-view.component.html',
    imports: [TableComponent],
})
export class DocumentViewComponent implements OnInit, OnChanges, OnDestroy {
    @Input() workflowAndRepo: Workflow | undefined;
    @Input() states: number[] = [];
    @Input() showMyItems: boolean = false;

    filterApplied: boolean = false;
    resultCount: number = 0;
    selectedBulkData: BulkRequestItem[] = [];
    selectedAllAcrossPages: boolean = false;
    private deselectedDocs: Map<
        number,
        { all: boolean; discrepancies: Set<number> }
    > = new Map();
    firstFetch = true;
    savedPageIndex: number | undefined;
    protected totalDiscrepancyCount: number | null = null;
    protected error = false;
    protected columnDefs: ColDef[] = [];
    protected gridOptions: GridOptions = {
        keepDetailRows: true,
        onRowSelected: this.onRowSelected.bind(this),
        onRowGroupOpened: this.onRowGroupOpened.bind(this),
        onPaginationChanged: () => {
            // Reapply visuals when page changes
            this.reapplyNormalVisuals();
        },
        onFilterChanged: () => {
            this.reapplyNormalVisuals();
        },
        onModelUpdated: () => {
            this.reapplyNormalVisuals();
        },
    };
    protected detailCellRendererParams = (p: ICellRendererParams) =>
        this.getDetailCellRendererParams(p);
    private detailSelections: { [masterRowId: number]: any[] } = {};
    private hiddenColumnOptions: string[] = [];
    private hiddenNestedColumnOptions: Set<string> = new Set([]);
    private readonly filteredDiscrepancyCounts: Map<number, number> = new Map();
    private lastFilterSignature: string | null = null;
    private lastStatesSignature: string | null = null;
    private _gridApi!: GridApi;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly validEventSources = ['checkboxSelected', 'uiSelectAll'];
    readonly dataQa = DATA_QA;

    private readonly store = inject(Store);
    private readonly ngZone = inject(NgZone);
    private readonly userService = inject(UserService);
    private readonly userDataService = inject(UserDataService);
    private readonly routerService = inject(RouterService);
    private readonly documentService = inject(DocumentService);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly documentDataService = inject(DocumentDataService);
    private readonly columnOptionsService = inject(ColumnOptionsService);
    private readonly workflowService = inject(WorkflowService);

    private customColumns: CustomColumn[] = [];
    private currentFilterOptions: DocumentFilterOptions =
        DocumentFilterOptions.initial();

    get filterModel() {
        return this._gridApi.getFilterModel();
    }

    get columnState() {
        return this._gridApi.getColumnState();
    }

    get hasSelectedRow() {
        return this.selectedAllAcrossPages
            ? (this.totalDiscrepancyCount ?? 0) > 0
            : this.selectedBulkData.length > 0;
    }

    get selectedRowsCount() {
        return this.selectedBulkData.length;
    }

    get selectedDiscrepancyCount() {
        if (this.selectedAllAcrossPages) {
            const total = this.totalDiscrepancyCount ?? 0; // already filtered by states
            let deselected = 0;
            this.deselectedDocs.forEach((value, documentId) => {
                const totalUnderDoc = this.getFilteredCountForDoc(
                    documentId,
                    value.discrepancies.size,
                );
                deselected += value.all
                    ? totalUnderDoc
                    : value.discrepancies.size;
            });
            return Math.max(total - deselected, 0);
        }
        return this.selectedBulkData.reduce(
            (n, { totalDiscCount }) => n + (totalDiscCount ?? 0),
            0,
        );
    }

    private buildBulkAction(): BulkActionRequest {
        if (this.selectedAllAcrossPages) {
            const documentAndDiscrepancies: {
                documentId: number;
                discrepancies: number[] | null;
            }[] = [];
            this.deselectedDocs.forEach((value, documentId) => {
                if (value.all) {
                    documentAndDiscrepancies.push({
                        documentId,
                        discrepancies: null,
                    });
                } else if (value.discrepancies.size > 0) {
                    documentAndDiscrepancies.push({
                        documentId,
                        discrepancies: Array.from(value.discrepancies.values()),
                    });
                }
            });
            return { selectedAll: true, documentAndDiscrepancies };
        }
        const documentAndDiscrepancies: {
            documentId: number;
            discrepancies: number[] | null;
        }[] = [];
        const selectedMasterIds: Set<number> = new Set();
        this._gridApi.getSelectedNodes().forEach((node) => {
            if (node.master && node.data) selectedMasterIds.add(node.data.id);
        });
        selectedMasterIds.forEach((documentId) => {
            const masterNode = this._gridApi.getRowNode(documentId.toString());
            const rawTotal: number = masterNode?.data?.discrepanciesCount ?? 0;
            let totalUnderDoc: number;
            if (
                this.states?.length > 0 &&
                !this.filteredDiscrepancyCounts.has(documentId)
            ) {
                // Avoid flicker: do not show raw total before filtered count arrives
                totalUnderDoc = 0;
            } else {
                totalUnderDoc = this.getFilteredCountForDoc(
                    documentId,
                    rawTotal,
                );
            }
            const selectedDetails = this.detailSelections[documentId];
            if (!selectedDetails || selectedDetails.length === 0) {
                documentAndDiscrepancies.push({
                    documentId,
                    discrepancies: null,
                });
            } else if (
                selectedDetails.length === totalUnderDoc &&
                totalUnderDoc > 0
            ) {
                documentAndDiscrepancies.push({
                    documentId,
                    discrepancies: null,
                });
            } else {
                documentAndDiscrepancies.push({
                    documentId,
                    discrepancies: selectedDetails.map((d: any) => d.id),
                });
            }
        });
        return { selectedAll: false, documentAndDiscrepancies };
    }

    buildBulkRequest(): BulkTakeDiscrepancyRequest {
        const action = this.buildBulkAction();
        const fm = this.filterModel;
        const q: DocumentViewQueryParameters = {
            name: fm['name']?.filter ?? null,
            documentType: fm['documentType']?.filter ?? null,
            workflowId: this.workflowAndRepo?.workflowId ?? null,
            states: this.states ?? [],
            showOnlyUserItems: this.showMyItems,
            priority: this.parseNumberOrNull(fm['priority']?.filter),
            repositoryId: this.parseNumberOrNull(fm['repository']?.filter),
            stationId: this.parseNumberOrNull(fm['station']?.filter),
            createdDateRangeFilter: fm['createdAt']?.filter ?? null,
            ageRangeFilter: fm['age']?.filter ?? null,
            discrepanciesCount: this.parseNumberOrNull(
                fm['discrepanciesCount']?.filter,
            ),
        };
        return {
            action: action,
            documentViewQueryParameters: q,
            discrepancyViewQueryParameters: null,
        };
    }

    private parseNumberOrNull(value: any): number | null {
        if (value === undefined || value === null || value === '') return null;
        const n = Number(value);
        return isNaN(n) ? null : n;
    }

    ngOnInit(): void {
        this.columnDefs = [
            ...this.getColumnDefs(DocumentFilterOptions.initial()),
        ];
        this.handleColumnOptions();
        this.getFilterOptions();
        this.fetchCustomColumns();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const workflowAndRepoChange = changes['workflowAndRepo'];
        if (workflowAndRepoChange?.previousValue) {
            this.error = false;
            this.resetFilters();
            this.getFilterOptions(true);
            this.fetchCustomColumns();
        }
    }

    ngOnDestroy() {
        this._subscriptions['get-inbox-choices'] = this.userService
            .getInboxChoices()
            .subscribe({
                complete: () => {
                    this._subscriptions.unsubscribeAllSafe();
                },
            });
    }

    onGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        const datasource = this.getDocuments();
        this._gridApi.setGridOption('serverSideDatasource', datasource);
        this.applySavedColumnVisibility();
    }

    setBulkData() {
        const rows: BulkRequestItem[] = [];

        if (this._gridApi)
            this._gridApi.forEachNode((rowNode: any) => {
                if (rowNode.data) {
                    const { id: documentId, discrepanciesCount } =
                        rowNode.data as WorkflowDocument;

                    if (rowNode.isSelected()) {
                        const discList: number[] = [];
                        const detailGridInfo =
                            rowNode.detailNode?.detailGridInfo;

                        if (detailGridInfo) {
                            detailGridInfo?.api.forEachNode(
                                (childNode: IRowNode) => {
                                    if (childNode.isSelected()) {
                                        discList.push(childNode.data.id);
                                    }
                                },
                            );
                        } else {
                            const detailSelected = Object.hasOwn(
                                this.detailSelections,
                                documentId,
                            );
                            if (detailSelected) {
                                discList.push(
                                    ...this.detailSelections[documentId].map(
                                        (i) => i.id,
                                    ),
                                );
                            }
                        }

                        rows.push({
                            documentId,
                            discrepancies: discList,
                            totalDiscCount: (() => {
                                if (discList.length > 0) {
                                    return discList.length;
                                }
                                const needsFilteredCount =
                                    this.states?.length > 0 &&
                                    !this.filteredDiscrepancyCounts.has(
                                        documentId,
                                    );
                                if (needsFilteredCount) {
                                    // Hold at 0 until filtered count fetched to prevent flicker
                                    return 0;
                                }
                                return this.getFilteredCountForDoc(
                                    documentId,
                                    discrepanciesCount ?? 0,
                                );
                            })(),
                        });
                        if (
                            this.states?.length > 0 &&
                            !this.filteredDiscrepancyCounts.has(documentId)
                        ) {
                            this.ensureFilteredCount(documentId, true);
                        }
                    }
                }
            });

        this.selectedBulkData = rows;
    }

    /**
     * Drops selected discrepancies whose workflow status matches one of the
     * removed status chips, keeping every other selection intact. Masters that
     * end up with no selected discrepancies are dropped and re-synced by the
     * existing re-apply logic when the table reloads. Called by the parent when
     * a status chip is de-selected.
     */
    pruneSelectionByStatuses(statuses: string[]) {
        if (!statuses.length || this.selectedAllAcrossPages) return;
        const removed = new Set(statuses);

        Object.keys(this.detailSelections).forEach((key) => {
            const documentId = Number(key);
            const kept = this.detailSelections[documentId].filter(
                (item: any) => !removed.has(item?.status),
            );
            if (kept.length) {
                this.detailSelections[documentId] = kept;
            } else {
                delete this.detailSelections[documentId];
            }
        });

        this.setBulkData();
    }

    resetFilters() {
        if (!this._gridApi.isDestroyed()) {
            this._gridApi?.refreshHeader();
            this._gridApi?.setFilterModel(null);
            this._gridApi?.onFilterChanged();
        }
    }

    refreshData(purge = true) {
        if (!this._gridApi.isDestroyed()) {
            this._gridApi.refreshServerSide({ purge });
        }
    }

    closePanel() {
        this.store.dispatch(
            saveSummaryPanelData({
                selectedDocument: null,
                selectedDiscrepancy: null,
            }),
        );
    }

    private setSavedFilter() {
        this._subscriptions['set-saved-filter'] =
            this.userDataService.userInboxChoices$.subscribe({
                next: (choices) => {
                    if (choices) {
                        this.savedPageIndex = choices.document.pageIndex ?? 1;

                        setFilterAndSorting(
                            this._gridApi,
                            pickFromEnum(choices.document, {
                                ...DocumentColumn,
                                order: 'order',
                                orderBy: 'orderBy',
                            }),
                        );
                        this.columnOptionsService.syncColumnConfig(
                            choices.columnConfig,
                        );
                    }
                },
            });
    }

    private onRowGroupOpened(event: any) {
        if (!event.expanded || this.selectedAllAcrossPages) return;
        const masterNode = event.node;
        if (!masterNode?.isSelected()) return;

        const docId = masterNode.data?.id;
        const savedSelections = this.detailSelections[docId];

        // If there are saved partial selections, keepDetailRows preserves them
        if (Array.isArray(savedSelections) && savedSelections.length > 0) {
            return;
        }

        // Master is selected but no saved detail selections — select all children
        const detailInfo: any = masterNode.detailNode?.detailGridInfo;
        if (detailInfo?.api && detailInfo.api.getDisplayedRowCount() > 0) {
            detailInfo.api.forEachNode((childNode: IRowNode) => {
                childNode.setSelected(true);
            });
        }
    }

    private onRowSelected(event: any) {
        if (this.selectionSyncInProgress) return;
        if (event?.source === 'paginationChanged') return;

        if (event?.source === 'uiSelectAll') {
            this.handleUiSelectAllToggle(event);
        }

        if (this.selectedAllAcrossPages) {
            this.handleGlobalModeSelection(event);
        } else {
            this.handleNormalModeSelection(event);
        }
    }

    private handleUiSelectAllToggle(event: any) {
        if (event.node?.selected && !this.selectedAllAcrossPages) {
            this.selectedAllAcrossPages = true;
            this.deselectedDocs.clear();
        } else if (!event.node?.selected && this.selectedAllAcrossPages) {
            this.selectedAllAcrossPages = false;
            this.deselectedDocs.clear();
            if (this._gridApi) {
                this._gridApi.deselectAll();
            }
            this.detailSelections = {};
            this.deselectAllDetailGridsAndIndeterminate();
        }
    }

    private handleGlobalModeSelection(event: any) {
        this.handleGlobalSelectionEvent(event);
        this.selectedBulkData = [];
        this.syncGlobalSelectionToVisibleRows(true);
    }

    private handleNormalModeSelection(event: any) {
        const masterNodeSelected =
            event?.node?.master &&
            event.node.expanded &&
            this.validEventSources.includes(event.source);

        if (masterNodeSelected) {
            this.selectAllChildNodes(event);
        } else {
            this.selectMasterNode(event);
        }
        this.setBulkData();
    }

    private selectionSyncInProgress = false;
    private skipProgrammaticSelection: boolean = false;
    private readonly lazyMasterApply: Set<number> = new Set();
    private readonly pendingMasterUpdates: Set<number> = new Set();
    private readonly pendingDiscrepancyUpdates: Map<number, Set<number>> =
        new Map();
    private pendingFullRescan: boolean = false;
    private skipMasterSelectionHandlingDocId: number | null = null;

    private syncGlobalSelectionToVisibleRows(immediate: boolean = false) {
        if (!this.selectedAllAcrossPages || !this._gridApi) return;
        const exec = () => {
            if (!this.shouldBeginGlobalSync()) return;
            this.selectionSyncInProgress = true;
            try {
                if (this.performFullRescanIfNeeded()) return; // full rescan consumed diff sets
                this.processPendingMasterUpdates();
                this.processPendingDiscrepancyUpdates();
                this.finalizeGlobalSelectionSync();
            } finally {
                this.selectionSyncInProgress = false;
            }
        };
        if (immediate) exec();
        else requestAnimationFrame(exec);
    }

    // Guard to determine if we should start a global sync operation.
    private shouldBeginGlobalSync(): boolean {
        return (
            this.selectedAllAcrossPages &&
            !!this._gridApi &&
            !this.selectionSyncInProgress
        );
    }

    // Returns true if a full rescan was performed (and diff sets cleared), signaling caller to exit early.
    private performFullRescanIfNeeded(): boolean {
        if (!this.pendingFullRescan) return false;
        this.runFullScan();
        this.pendingFullRescan = false;
        this.clearDiffSets();
        return true;
    }

    // Apply master node selection updates accumulated in pendingMasterUpdates.
    private processPendingMasterUpdates(): void {
        this.pendingMasterUpdates.forEach((docId) => {
            const node = this._gridApi.getRowNode(docId.toString());
            if (!node?.data) return;
            this.applyMasterNodeSelection(node);
        });
    }

    // Apply discrepancy (child) selection updates; when detail grids not yet loaded, mark for lazy application.
    private processPendingDiscrepancyUpdates(): void {
        this.pendingDiscrepancyUpdates.forEach((discSet, docId) => {
            const masterNode = this._gridApi.getRowNode(docId.toString());
            const detailInfo: any = (masterNode as any)?.detailNode
                ?.detailGridInfo;
            if (!detailInfo?.api) {
                this.lazyMasterApply.add(docId);
                return;
            }
            discSet.forEach((discId) => {
                const rowNode = detailInfo.api.getRowNode('nested-' + discId);
                if (!rowNode?.data) return;
                this.applyDiscrepancyNodeSelection(rowNode, docId);
            });
            this.updateMasterIndeterminate(masterNode, docId);
        });
    }

    private finalizeGlobalSelectionSync(): void {
        this._gridApi.forEachNode((node: IRowNode) => {
            if (node.master && node.data) {
                this.updateMasterIndeterminate(node, node.data.id);
            }
        });
        this._gridApi.refreshHeader();
        // Sanitize any erroneous indeterminate styles on leaf discrepancy rows.
        this.removeIndeterminateFromLeafRows();
        this.clearDiffSets();
    }

    private applyMasterNodeSelection(node: IRowNode) {
        const docId = node.data.id;
        const entry = this.deselectedDocs.get(docId);
        const shouldSelect = !entry?.all;
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
        this.updateMasterIndeterminate(node, docId);
    }

    private applyDiscrepancyNodeSelection(rowNode: IRowNode, docId: number) {
        const shouldSelect = this.computeDiscrepancyShouldSelect(
            docId,
            rowNode.data.id,
        );
        if (rowNode.isSelected() !== shouldSelect)
            rowNode.setSelected(shouldSelect);
    }

    private updateMasterIndeterminate(
        masterNode: IRowNode | undefined,
        docId: number,
    ) {
        if (!masterNode?.data) return;
        const entry = this.deselectedDocs.get(docId);
        const shouldSelect = !entry?.all;
        if (shouldSelect && (entry?.discrepancies?.size ?? 0) > 0) {
            this.markMasterRowIndeterminate(masterNode);
        } else {
            this.clearMasterRowIndeterminate(masterNode);
        }
    }

    private computeDiscrepancyShouldSelect(docId: number, discId: number) {
        const entry = this.deselectedDocs.get(docId);
        if (entry?.all) return false;
        if (entry?.discrepancies?.has(discId)) return false;
        return true;
    }

    private runFullScan() {
        let changed = false;
        this._gridApi.forEachNode((node: IRowNode) => {
            if (!node.data) return;
            if (node.master) {
                this.applyMasterNodeSelection(node);
            } else {
                const docId = node.data.documentId;
                const entry = this.deselectedDocs.get(docId);
                const shouldSelect = this.computeDiscrepancyShouldSelect(
                    docId,
                    node.data.id,
                );
                if (node.isSelected() !== shouldSelect) {
                    node.setSelected(shouldSelect);
                    changed = true;
                }
            }
        });
        if (this.deselectedDocs.size === 0) {
            if (!changed) this._gridApi.selectAll();
            this._gridApi.forEachNode((n: IRowNode) => {
                if (!n.data) return;
                if (!n.isSelected()) n.setSelected(true);
            });
        }
        this._gridApi.refreshHeader();
    }

    private clearDiffSets() {
        this.pendingMasterUpdates.clear();
        this.pendingDiscrepancyUpdates.clear();
        this.lazyMasterApply.clear();
    }

    private handleGlobalSelectionEvent(event: any) {
        if (!event?.node) return;
        const node = event.node as IRowNode;
        if (node.master && node.data) {
            this.handleGlobalMasterNode(node);
            return;
        }
        if (node.data && (node.data as any).documentId) {
            this.handleGlobalDiscrepancyNode(node as any);
        }
    }

    private handleGlobalMasterNode(node: IRowNode) {
        const documentId = node.data.id;
        // Skip master handling if flagged due to programmatic selection during partial conversion.
        if (this.skipMasterSelectionHandlingDocId === documentId) {
            this.skipMasterSelectionHandlingDocId = null; // consume flag
            return;
        }
        if (!node.isSelected()) {
            this.deselectedDocs.set(documentId, {
                all: true,
                discrepancies: new Set(),
            });
            // Preload filtered discrepancy count so global selected count updates immediately
            this.ensureFilteredCount(documentId);
        } else {
            this.deselectedDocs.delete(documentId);
        }
        this.applyMasterSelectionToChildren(node);
        this.pendingMasterUpdates.add(documentId);
    }

    private handleGlobalDiscrepancyNode(node: IRowNode) {
        const documentId = (node.data as any).documentId as number;
        const detailId = (node.data as any).id as number;
        const masterRow = this._gridApi.getRowNode(documentId.toString());
        const totalUnderDoc: number = this.getFilteredCountForDoc(
            documentId,
            masterRow?.data?.discrepanciesCount ?? 0,
        );
        const entry = this.deselectedDocs.get(documentId);
        // If the document was fully deselected, handle that case and exit early
        if (
            this.handleFullDeselectCase(
                documentId,
                detailId,
                masterRow ?? null,
                node,
                entry,
            )
        )
            return;

        if (!node.isSelected()) {
            this.handleChildDeselected(documentId, detailId, totalUnderDoc);
        } else {
            this.handleChildSelected(documentId, entry, detailId);
        }

        this.addPendingDiscrepancyUpdate(documentId, detailId);
        this.pendingMasterUpdates.add(documentId);
    }

    private handleFullDeselectCase(
        documentId: number,
        detailId: number,
        masterRow: IRowNode | null,
        node: IRowNode,
        entry?: { all: boolean; discrepancies: Set<number> },
    ) {
        if (!entry?.all) return false;
        if (node.isSelected()) {
            this.convertFullDeselectToPartial(
                documentId,
                detailId,
                masterRow ?? null,
                node,
            );
        }
        // whether user selected or deselected the child while doc fully deselected, we've handled it
        return true;
    }

    private handleChildDeselected(
        documentId: number,
        detailId: number,
        totalUnderDoc: number,
    ) {
        this.addDeselected(documentId, detailId);
        const record = this.deselectedDocs.get(documentId)!;
        if (record.discrepancies.size === totalUnderDoc && totalUnderDoc > 0) {
            this.deselectedDocs.set(documentId, {
                all: true,
                discrepancies: new Set(),
            });
            const masterNode = this._gridApi.getRowNode(documentId.toString());
            if (masterNode) this.applyMasterSelectionToChildren(masterNode);
        }
    }

    private handleChildSelected(
        documentId: number,
        entry: { all: boolean; discrepancies: Set<number> } | undefined,
        detailId: number,
    ) {
        if (!entry) return;
        entry.discrepancies.delete(detailId);
        if (entry.discrepancies.size === 0) {
            this.deselectedDocs.delete(documentId);
            const masterNode = this._gridApi.getRowNode(documentId.toString());
            if (masterNode) this.applyMasterSelectionToChildren(masterNode);
        }
    }

    private convertFullDeselectToPartial(
        documentId: number,
        selectedDetailId: number,
        masterRow: IRowNode | null,
        node: IRowNode,
    ) {
        const detailInfo: any = (masterRow as any)?.detailNode?.detailGridInfo;
        const api = detailInfo?.api;
        const newSet = new Set<number>();
        if (api) {
            api.forEachNode((child: IRowNode) => {
                if (!child.data) return;
                const childId = child.data.id;
                if (childId !== selectedDetailId) newSet.add(childId);
            });
        }

        // Convert entry to partial
        const entry = this.deselectedDocs.get(documentId)!;
        entry.all = false;
        entry.discrepancies = newSet;
        if (entry.discrepancies.size === 0)
            this.deselectedDocs.delete(documentId);

        // Ensure master row selected without triggering its handler
        if (masterRow) {
            if (!masterRow.isSelected()) {
                this.skipMasterSelectionHandlingDocId = documentId;
                masterRow.setSelected(true);
            }
            this.updateMasterIndeterminate(masterRow, documentId);
            this.pendingMasterUpdates.add(documentId);
        }
        // Ensure filtered count exists for partial state calculations
        this.ensureFilteredCount(documentId);

        // Diff: include selected child and other children as exceptions
        if (!this.pendingDiscrepancyUpdates.has(documentId))
            this.pendingDiscrepancyUpdates.set(documentId, new Set());
        const diffSet = this.pendingDiscrepancyUpdates.get(documentId)!;
        diffSet.add(selectedDetailId);
        newSet.forEach((id) => diffSet.add(id));

        // Immediately deselect other loaded children to avoid cascade selecting all
        if (api && newSet.size > 0) {
            this.skipProgrammaticSelection = true;
            api.forEachNode((child: IRowNode) => {
                if (!child.data) return;
                const cid = child.data.id;
                if (
                    cid !== selectedDetailId &&
                    newSet.has(cid) &&
                    child.isSelected()
                ) {
                    child.setSelected(false);
                }
            });
            this.skipProgrammaticSelection = false;
        }
    }

    private addDeselected(documentId: number, detailId: number) {
        let record = this.deselectedDocs.get(documentId);
        if (!record) {
            record = { all: false, discrepancies: new Set() };
            this.deselectedDocs.set(documentId, record);
            // We are entering a partial state; ensure filtered count present.
            this.ensureFilteredCount(documentId);
        }
        record.discrepancies.add(detailId);
    }

    private addPendingDiscrepancyUpdate(documentId: number, detailId: number) {
        if (!this.pendingDiscrepancyUpdates.has(documentId)) {
            this.pendingDiscrepancyUpdates.set(documentId, new Set());
        }
        this.pendingDiscrepancyUpdates.get(documentId)!.add(detailId);
    }

    private applyMasterSelectionToChildren(masterNode: IRowNode) {
        const docId = masterNode?.data?.id;
        if (docId === undefined) return;
        const detailGridInfo: any = (masterNode as any).detailNode
            ?.detailGridInfo;
        if (!detailGridInfo?.api) return;
        const entry = this.deselectedDocs.get(docId);
        const shouldSelect = !entry?.all;
        detailGridInfo.api.forEachNode((child: IRowNode) => {
            if (!child.data) return;
            if (child.isSelected() !== shouldSelect)
                child.setSelected(shouldSelect);
        });
        if (shouldSelect && entry && entry.discrepancies.size > 0) {
            this.markMasterRowIndeterminate(masterNode);
        } else {
            this.clearMasterRowIndeterminate(masterNode);
        }
    }

    private getFilteredCountForDoc(documentId: number, fallback: number) {
        return this.filteredDiscrepancyCounts.get(documentId) ?? fallback;
    }

    private ensureFilteredCount(
        documentId: number,
        refreshBulk: boolean = false,
    ) {
        if (this.filteredDiscrepancyCounts.has(documentId)) return;
        // Fetch discrepancies (filtered by states & mine flags) to establish accurate filtered count without expansion.
        this.discrepancyService
            .getDiscrepanciesByDocumentId(documentId, {
                mine: this.showMyItems,
                states: this.states,
            })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.ngZone.run(() => {
                        this.filteredDiscrepancyCounts.set(
                            documentId,
                            response.discrepancies.length,
                        );
                        if (refreshBulk && !this.selectedAllAcrossPages) {
                            this.setBulkData();
                        }
                        // After filtered count arrives, reapply normal-mode partial visuals.
                        if (!this.selectedAllAcrossPages) {
                            this.applyNormalIndeterminateFromSaved();
                        }
                    });
                },
            });
    }

    private markMasterRowIndeterminate(masterNode: IRowNode) {
        this.applyIndeterminateClass(masterNode.rowIndex);
    }

    private clearMasterRowIndeterminate(masterNode: IRowNode) {
        this.removeIndeterminateClass(masterNode.rowIndex);
    }

    private onDetailSelectionChanged(event: any) {
        const masterRowId: number | null = event?.data?.documentId ?? null;
        const selectedNodes = event.api.getSelectedNodes();
        const selectedData = selectedNodes.map(
            (node: { data: any }) => node.data,
        );

        if (masterRowId != null)
            this.detailSelections[masterRowId] = selectedData;
        // In normal mode, keep master selection/visuals in sync with child selections
        if (!this.selectedAllAcrossPages && masterRowId != null) {
            this.syncMasterFromDetail(masterRowId, selectedData.length);
        }
    }

    private syncMasterFromDetail(masterRowId: number, selectedCount: number) {
        const masterNode = this._gridApi.getRowNode(masterRowId.toString());
        if (!masterNode) return;
        if (selectedCount === 0) {
            if (masterNode.isSelected()) masterNode.setSelected(false);
            this.clearMasterRowIndeterminate(masterNode);
            return;
        }
        if (!masterNode.isSelected()) masterNode.setSelected(true);
        const total = this.getFilteredCountForDoc(
            masterRowId,
            (masterNode.data?.discrepanciesCount ?? 0) as number,
        );
        this.updateNormalMasterIndeterminate(
            masterNode,
            masterRowId,
            selectedCount,
            total,
        );
    }

    private selectAllChildNodes(event: any) {
        const gridDetailGridInfo: DetailGridInfo = event.api.getDetailGridInfo(
            event.node.detailNode.id,
        );

        if (gridDetailGridInfo?.api) {
            gridDetailGridInfo.api.forEachNode((node: IRowNode) => {
                node.setSelected(event.node.selected);
            });
        }
    }

    // Common helper: reapply global sync and normal-mode partial visuals
    private reapplyNormalVisuals() {
        this.syncGlobalSelectionToVisibleRows();
        this.applyNormalIndeterminateFromSaved();
    }

    // Common helper: apply partial visual for a master row using saved selections
    private applyPartialForMasterFromSaved(node: IRowNode) {
        if (!node?.data) return;
        const docId = node.data.id as number;
        const selectedData = this.detailSelections[docId];
        if (Array.isArray(selectedData) && selectedData.length > 0) {
            const total = this.getFilteredCountForDoc(
                docId,
                (node.data?.discrepanciesCount ?? 0) as number,
            );
            const partial =
                selectedData.length > 0 && selectedData.length < total;
            if (partial) this.applyIndeterminateClass(node.rowIndex);
            else this.removeIndeterminateClass(node.rowIndex);
        } else {
            this.removeIndeterminateClass(node.rowIndex);
        }
    }

    private selectMasterNode(event: any) {
        const numericId: number | null = event?.node?.master
            ? (event?.node?.data?.id as number)
            : (event?.data?.documentId ?? null);
        if (numericId == null) return;
        const masterNode = this._gridApi.getRowNode(numericId.toString());
        if (!masterNode) return;

        if (
            event?.node?.master &&
            this.validEventSources.includes(event.source)
        ) {
            this.handleMasterCheckboxClick(event, masterNode, numericId);
        } else if (!event?.node?.master) {
            this.handleDetailSelectionChange(event, masterNode);
        }
        this.preloadFilteredCountIfNeeded(numericId);
    }

    private handleMasterCheckboxClick(
        event: any,
        masterNode: IRowNode,
        numericId: number,
    ) {
        const detailInfo: any = (masterNode as any).detailNode?.detailGridInfo;

        if (!event.node.selected) {
            masterNode.setSelected(false);
            this.clearMasterRowIndeterminate(masterNode);
            delete this.detailSelections[numericId];
            if (detailInfo?.api) {
                detailInfo.api.deselectAll();
            }
            return;
        }

        masterNode.setSelected(true);
        this.clearMasterRowIndeterminate(masterNode);
        if (detailInfo?.api) {
            detailInfo.api.forEachNode((childNode: IRowNode) => {
                childNode.setSelected(true);
            });
        }
    }

    private handleDetailSelectionChange(event: any, masterNode: IRowNode) {
        const api = event.api;
        const selectedNodesCount = api.getSelectedNodes().length;
        const renderedNodesCount = api.getRenderedNodes().length;

        if (selectedNodesCount === 0) {
            masterNode.setSelected(false);
            this.clearMasterRowIndeterminate(masterNode);
            return;
        }

        masterNode.setSelected(true);
        const isPartial = selectedNodesCount < renderedNodesCount;
        if (isPartial) this.markMasterRowIndeterminate(masterNode);
        else this.clearMasterRowIndeterminate(masterNode);
    }

    private preloadFilteredCountIfNeeded(documentId: number) {
        if (
            this.states?.length > 0 &&
            !this.filteredDiscrepancyCounts.has(documentId)
        ) {
            this.ensureFilteredCount(documentId, true);
        }
    }

    private handleColumnOptions() {
        this._subscriptions['column-options'] =
            this.columnOptionsService.columnOptions$.subscribe({
                next: (options) => {
                    this.hiddenColumnOptions = options.hiddenColumns;
                    this.applySavedColumnVisibility();

                    const option = options.lastToggled;
                    if (
                        option &&
                        this._gridApi &&
                        !this._gridApi.isDestroyed()
                    ) {
                        this._gridApi.setColumnsVisible(
                            [option.id],
                            option.checked,
                        );
                        // this._gridApi.destroyFilter(option.id);
                    }
                },
            });

        this._subscriptions['nested-column-options'] =
            this.columnOptionsService.nestedColumnOptions$.subscribe({
                next: (options: { hiddenColumns: string[] }) => {
                    this.hiddenNestedColumnOptions = new Set(
                        options.hiddenColumns.map((id) => id.toLowerCase()),
                    );
                    // detailCellRendererParams is already a closure over `this`;
                    // no need to reassign — newly opened rows will call
                    // getDetailColumnDefs() which reads hiddenNestedColumnOptions.

                    // Apply visibility to already-open detail grids.
                    if (this._gridApi && !this._gridApi.isDestroyed()) {
                        const hiddenSet = this.hiddenNestedColumnOptions;
                        this._gridApi.forEachNode((masterNode) => {
                            const detailApi: any = (masterNode as any)
                                ?.detailNode?.detailGridInfo?.api;
                            if (!detailApi || detailApi.isDestroyed()) return;
                            const allColIds =
                                detailApi
                                    .getColumns()
                                    ?.map((c: any) => c.getColId()) ?? [];
                            const toShow = allColIds.filter(
                                (id: string) =>
                                    !hiddenSet.has(id.toLowerCase()),
                            );
                            const toHide = allColIds.filter((id: string) =>
                                hiddenSet.has(id.toLowerCase()),
                            );
                            if (toShow.length)
                                detailApi.setColumnsVisible(toShow, true);
                            if (toHide.length)
                                detailApi.setColumnsVisible(toHide, false);
                        });
                    }
                },
            });
    }

    private getFilterOptions(ignoreUserChoices = false) {
        this._subscriptions.unsubscribeSafe('filter-options');

        this._subscriptions['filter-options'] = this.documentService
            .getDocumentsFilterOptions(this.workflowAndRepo)
            .subscribe({
                next: (response) => {
                    this.currentFilterOptions = response;
                    this.columnDefs = this.getColumnDefs(response);
                    // Defer visibility restore: ag-Grid processes new columnDefs
                    // during Angular CD (after this callback), so setTimeout ensures
                    // setColumnsVisible runs after the grid has rebuilt its columns.
                    setTimeout(() => this.applySavedColumnVisibility());
                },
                complete: () => {
                    if (!ignoreUserChoices) this.setSavedFilter();
                },
            });
    }

    private fetchCustomColumns() {
        const workflowId = this.workflowAndRepo?.workflowId;
        if (!workflowId) {
            this.customColumns = [];
            return;
        }
        this._subscriptions.unsubscribeSafe('custom-columns');
        this._subscriptions['custom-columns'] = this.workflowService
            .getCustomColumns(workflowId)
            .subscribe({
                next: (columns: CustomColumn[]) => {
                    this.customColumns = columns;

                    // Only update detail (nested discrepancy) grid — main document grid
                    // does not show custom columns.
                    this.detailCellRendererParams = (p: ICellRendererParams) =>
                        this.getDetailCellRendererParams(p);
                },
            });
    }

    private applySavedColumnVisibility() {
        hideHiddenColumns(this._gridApi, this.hiddenColumnOptions);
    }

    private getDocuments(): IServerSideDatasource {
        this._subscriptions.unsubscribeSafe('get-documents');
        this.error = false;

        return {
            getRows: (params) => {
                params.api.hideOverlay();
                this.filterApplied = isFilterApplied(params.request);
                const currentSignature = JSON.stringify(
                    params.request.filterModel || {},
                );
                const filterChanged =
                    this.lastFilterSignature !== currentSignature;
                const statesSignature = JSON.stringify(this.states ?? []);
                const statesChanged =
                    this.lastStatesSignature !== statesSignature;
                if (filterChanged || statesChanged) {
                    this.lastFilterSignature = currentSignature;
                    this.lastStatesSignature = statesSignature;
                    if (this.selectedAllAcrossPages) {
                        // Select-all mode: keep "all selected" semantics across
                        // filter/chip changes by dropping explicit deselections
                        // and re-selecting the new result set.
                        this.deselectedDocs.clear();
                        this.pendingFullRescan = true;
                    } else if (filterChanged) {
                        // A column filter changed: reset the normal selection
                        // (existing behavior).
                        this._gridApi.deselectAll();
                        this.detailSelections = {};
                    }
                    // Status-chip (states) changes in normal mode are handled by
                    // the parent via pruneSelectionByStatuses() before the
                    // refresh (only the removed status is dropped); the retained
                    // selection is re-applied when detail rows reload, so we
                    // intentionally do not wipe here.
                }

                this.getTotalDiscCount(this.getQueryParams(params.request));

                this.documentService
                    .getDocuments(this.getQueryParams(params.request))
                    .pipe(first())
                    .subscribe({
                        next: (data) => {
                            params.api.hideOverlay();

                            if (data) {
                                params.success({
                                    rowData: data.items,
                                    rowCount: data.totalItems ?? -1,
                                });
                                if (data.items.length === 0) {
                                    params.api.showNoRowsOverlay();
                                }
                                this.resultCount = data.totalItems ?? 0;
                                // Preload filtered totals for masters with saved selections so partial visual is correct without expanding.
                                if (!this.selectedAllAcrossPages) {
                                    const docsNeedingCount: number[] = [];
                                    this._gridApi.forEachNode(
                                        (node: IRowNode) => {
                                            if (!node?.master || !node?.data)
                                                return;
                                            const docId = node.data
                                                .id as number;
                                            const selectedData =
                                                this.detailSelections[docId];
                                            if (
                                                selectedData &&
                                                selectedData.length > 0 &&
                                                !this.filteredDiscrepancyCounts.has(
                                                    docId,
                                                )
                                            ) {
                                                docsNeedingCount.push(docId);
                                            }
                                        },
                                    );
                                    docsNeedingCount.forEach((docId) =>
                                        this.ensureFilteredCount(docId),
                                    );
                                    // After a microtask, apply partial visuals using freshly populated counts.
                                    setTimeout(() =>
                                        this.applyNormalIndeterminateFromSaved(),
                                    );
                                } else {
                                    this.applyNormalIndeterminateFromSaved();
                                }
                            } else {
                                params.fail();
                            }
                        },
                        error: () => {
                            params.api.hideOverlay();
                            this.error = true;
                        },
                    });
            },
        };
    }

    private getTotalDiscCount(params: {
        pageIndex: number;
        pageSize: number;
        mine: boolean;
        states: number[];
        workflowId?: number;
        repositoryIds?: number[];
    }) {
        const { pageIndex, pageSize, ...rest } = params;

        this.documentService
            .getTotalCount(rest)
            .pipe(take(1))
            .subscribe({
                next: (response) =>
                    (this.totalDiscrepancyCount =
                        response?.discrepancyCount ?? null),
            });
    }

    private getDiscrepancies(params: GetDetailRowDataParams<WorkflowDocument>) {
        const documentId = params.data.id;

        this.discrepancyService
            .getDiscrepanciesByDocumentId(documentId, {
                mine: this.showMyItems,
                states: this.states,
            })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.ngZone.run(() => {
                        this.updateStatusBarDiscCount(
                            documentId,
                            response.discrepancies.length,
                            response.totalCount,
                        );
                        params.successCallback(response.discrepancies);

                        setTimeout(() => {
                            const selectedData =
                                this.detailSelections[documentId];
                            if (
                                Array.isArray(selectedData) &&
                                selectedData.length > 0
                            ) {
                                this.applySavedDetailSelections(params);
                            } else {
                                this.setSelectedChildRows(params.node);
                            }
                            // Lazy diff application for discrepancies loaded after selection changes
                            if (
                                this.selectedAllAcrossPages &&
                                this.lazyMasterApply.has(documentId)
                            ) {
                                this.applyPendingDiscrepanciesForDetail(
                                    params,
                                    documentId,
                                );
                            }
                            // Ensure no leaf discrepancy retains an indeterminate class.
                            this.removeIndeterminateFromLeafRows();
                        });
                    });
                },
            });
    }

    private applySavedDetailSelections(
        params: GetDetailRowDataParams<WorkflowDocument>,
    ) {
        const selectedData = this.detailSelections[params.data.id];
        const detailGridApi = (params.node as any).detailGridInfo?.api;

        if (detailGridApi && params.node)
            detailGridApi.forEachNode((node: IRowNode) => {
                if (selectedData && selectedData.length > 0) {
                    const isSelected = selectedData.some(
                        (d) => d.id === node.data.id,
                    );
                    if (isSelected) {
                        node.setSelected(true);
                    }
                }
            });
        if (
            !this.selectedAllAcrossPages &&
            selectedData &&
            selectedData.length > 0
        ) {
            const total = this.getFilteredCountForDoc(
                params.data.id,
                (params.node?.data?.discrepanciesCount ?? 0) as number,
            );
            this.updateNormalMasterIndeterminate(
                params.node,
                params.data.id,
                selectedData?.length ?? 0,
                total,
            );
        }
    }

    private applyPendingDiscrepanciesForDetail(
        params: GetDetailRowDataParams<WorkflowDocument>,
        documentId: number,
    ) {
        const discSet = this.pendingDiscrepancyUpdates.get(documentId);
        const detailInfo: any = (params.node as any).detailGridInfo;
        if (!discSet || !detailInfo?.api) return;
        discSet.forEach((discId) => {
            const rowNode = detailInfo.api.getRowNode('nested-' + discId);
            if (rowNode?.data)
                this.applyDiscrepancyNodeSelection(rowNode, documentId);
        });
        this.updateMasterIndeterminate(params.node, documentId);
        this.lazyMasterApply.delete(documentId);
    }

    private updateStatusBarDiscCount(
        documentId: number,
        filtered: number,
        total: number,
    ) {
        this.documentDataService.updateDiscDataForStatusPanel({
            documentId,
            filtered,
            total,
        });
        this.filteredDiscrepancyCounts.set(documentId, filtered);
    }

    private setSelectedChildRows(node: any) {
        const detailGridApi = node.detailGridInfo?.api;
        if (detailGridApi)
            detailGridApi.forEachNode((childNode: any) => {
                childNode.setSelected(node.parent?.isSelected());
            });
        if (!this.selectedAllAcrossPages) {
            this.applyPartialForMasterFromSaved(node);
        }
    }

    private getQueryParams(request: IServerSideGetRowsRequest) {
        const pageSize = this._gridApi.paginationGetPageSize();
        const pageIndex = getPageIndex(request, pageSize);

        return {
            ...this.workflowAndRepo,
            ...{ states: this.states },
            ...{ mine: this.showMyItems },
            ...getFilterQuery(request.filterModel),
            ...getSortQuery(request.sortModel),
            pageIndex,
            pageSize,
        };
    }

    private expandRow(node: IRowNode) {
        this._gridApi.setRowNodeExpanded(node, true);
    }

    private onDocumentNameClick(node: IRowNode) {
        const documentId = node.data.id;

        // Fetch discrepancies to get the first discrepancy ID
        this.discrepancyService
            .getDiscrepanciesByDocumentId(documentId, {
                mine: this.showMyItems,
                states: this.states,
            })
            .pipe(first())
            .subscribe({
                next: (response) => {
                    const firstDiscrepancyId =
                        response.discrepancies.length > 0
                            ? response.discrepancies[0].id
                            : null;

                    this.store.dispatch(
                        saveSummaryPanelData({
                            selectedDocument: documentId,
                            selectedDiscrepancy: firstDiscrepancyId,
                            selectedTab: DocumentDetailTab.DISCREPANCIES,
                        }),
                    );

                    if (firstDiscrepancyId) {
                        highlightSelectedDiscrepancy(firstDiscrepancyId);
                    }
                },
            });

        this.expandRow(node);
        this._gridApi.ensureIndexVisible(node.rowIndex ?? 0, 'top');
        if (!this.selectedAllAcrossPages) {
            const docId = node.data.id as number;
            const sd = this.detailSelections[docId];
            if (Array.isArray(sd) && sd.length === 0) {
                delete this.detailSelections[docId];
            }
            this.applyPartialForMasterFromSaved(node);
        }
    }

    private onDocumentNameDoubleClick(node: IRowNode) {
        const documentId = parseInt(node.data.id);

        this.documentService
            .canEditDocument(documentId)
            .pipe(first())
            .subscribe({
                next: (canEdit) => {
                    if (canEdit) {
                        // Fetch discrepancies to get the first discrepancy ID
                        this.discrepancyService
                            .getDiscrepanciesByDocumentId(documentId, {
                                mine: this.showMyItems,
                                states: this.states,
                            })
                            .pipe(first())
                            .subscribe({
                                next: (response) => {
                                    const firstDiscrepancyId =
                                        response.discrepancies.length > 0
                                            ? response.discrepancies[0].id
                                            : undefined;

                                    this.routerService.goDocumentDetails(
                                        documentId,
                                        DocumentDetailTab.DISCREPANCIES,
                                        firstDiscrepancyId,
                                    );
                                },
                            });
                    }
                },
            });
    }

    private onDiscrepancyIdClick(node: IRowNode) {
        this.store.dispatch(
            saveSummaryPanelData({
                selectedDocument: node.data.documentId,
                selectedDiscrepancy: node.data.id,
                selectedTab: DocumentDetailTab.DISCREPANCIES,
            }),
        );
        highlightSelectedDiscrepancy(node.data.id);
    }

    private onDiscrepancyIdDoubleClick(node: IRowNode) {
        this.documentService
            .canEditDocument(node.data.id)
            .pipe(first())
            .subscribe({
                next: (canEdit) => {
                    if (canEdit) {
                        this.routerService.goDocumentDetails(
                            parseInt(node.data.documentId),
                            DocumentDetailTab.DISCREPANCIES,
                            parseInt(node.data.id),
                        );
                    }
                },
            });
    }

    private getDetailCellRendererParams(p: ICellRendererParams) {
        return {
            detailGridOptions: this.getDetailGridOptions(p),
            getDetailRowData: (params: GetDetailRowDataParams) =>
                this.getDiscrepancies(params),
        };
    }

    private getColumnDefs(filterOptions: DocumentFilterOptions): ColDef[] {
        return [
            {
                colId: 'select',
                headerName: '',
                checkboxSelection: true,
                headerCheckboxSelection: true,
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                pinned: 'left' as const,
                filter: false,
                lockPosition: true,
                suppressMovable: true,
                resizable: false,
                suppressSizeToFit: true,
            },
            {
                field: DocumentColumn.NAME,
                headerName: 'Document Name',
                showRowGroup: true,
                cellRenderer: 'agGroupCellRenderer',
                cellRendererParams: {
                    suppressCount: true,
                    suppressDoubleClickExpand: true,
                    innerRenderer: ClickableCellComponent,
                    innerRendererParams: {
                        dataQa: this.dataQa.documentViewDocumentNameCell,
                        tooltipGetter: (params: ICellRendererParams) =>
                            params.data.name,
                        onClick: (node: IRowNode) =>
                            this.onDocumentNameClick(node),
                        onDoubleClick: (node: IRowNode) =>
                            this.onDocumentNameDoubleClick(node),
                    },
                },
                filter: 'agTextColumnFilter',
                floatingFilterComponent: TextFilterComponent,
                floatingFilterComponentParams: {
                    dataQa: this.dataQa.documentViewDocumentNameFilter,
                },
                minWidth: 180,
            },
            {
                field: DocumentColumn.DOC_TYPE,
                maxWidth: 250,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.documentType,
                    autocomplete: true,
                },
            },
            {
                field: DocumentColumn.PRIORITY,
                maxWidth: 130,
                cellRenderer: PriorityCellComponent,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.priority,
                    priority: true,
                },
            },
            {
                field: DocumentColumn.REPOSITORY,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: [...filterOptions.repository],
                },
            },
            {
                field: DocumentColumn.AGE_IN_WF,
                headerName: 'Age In WF',
                maxWidth: 120,
                cellRenderer: GrayBoxCellComponent,
                valueFormatter: getAgeInWFValue,
                cellStyle: { textOverflow: 'clip' },
                filter: 'agTextColumnFilter',
                floatingFilterComponent: RangeFilterComponent,
                floatingFilterComponentParams: {
                    availableRange: new Range(0, filterOptions.maxAge),
                    inputLabel: 'days',
                },
            },
            {
                field: DocumentColumn.WF_ENTER_DATE,
                headerName: 'WF Enter Date',
                maxWidth: 160,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: DateFilterComponent,
            },
            {
                field: DocumentColumn.DISCREPANCY_COUNT,
                maxWidth: 150,
                headerName: '# Discrepancies',
                filter: 'agTextColumnFilter',
                floatingFilterComponent: TextFilterComponent,
                floatingFilterComponentParams: {
                    type: 'number',
                },
            },
            {
                field: DocumentColumn.STATION,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.station,
                    autocomplete: true,
                    noneOption: true,
                },
            },
        ];
    }

    private getDetailGridOptions(params: ICellRendererParams) {
        return {
            rowHeight: 35,
            headerHeight: 32,
            rowSelection: 'multiple',
            suppressRowClickSelection: true,
            rowMultiSelectWithClick: true,

            onRowSelected: (e: any) => {
                this.onRowSelected(e);
                this.onDetailSelectionChanged(e);
            },
            columnDefs: this.getDetailColumnDefs(),
            getRowId: (params: GetRowIdParams) => 'nested-' + params.data.id,
            noRowsOverlayComponent: NoRowsOverlayComponent,
            noRowsOverlayComponentParams: {
                text: 'No discrepancies found',
                isDetail: true,
            },
            defaultColDef: {
                flex: 1,
                cellStyle: { display: 'flex', alignItems: 'center' },
            },
            statusBar: {
                statusPanels: [
                    {
                        statusPanel: CountStatusBarComponent,
                        statusPanelParams: { documentId: params.data.id },
                        align: 'left',
                    },
                ],
            },
        };
    }

    private getDetailColumnDefs(): ColDef[] {
        const columnDefs = [
            {
                colId: 'nested-select',
                headerName: '',
                checkboxSelection: true,
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                pinned: 'left' as const,
                lockPosition: true,
                suppressMovable: true,
                resizable: false,
                suppressSizeToFit: true,
            },
            {
                field: DocumentNestedColumn.ID,
                headerName: 'Discrepancy ID',
                valueFormatter: (p: { data: Discrepancy }) =>
                    getDiscrepancyId(p.data).label,
                cellRenderer: ClickableCellComponent,
                cellRendererParams: {
                    dataQa: this.dataQa.documentViewNestedDiscrepanyIdCell,
                    tooltipGetter: (p: ICellRendererParams) =>
                        getDiscrepancyId(p.data).tooltip,
                    onClick: (node: IRowNode) =>
                        this.onDiscrepancyIdClick(node),
                    onDoubleClick: (node: IRowNode) =>
                        this.onDiscrepancyIdDoubleClick(node),
                },
            },
            { field: DocumentNestedColumn.REASON },
            {
                field: DocumentNestedColumn.PRIORITY,
                cellRenderer: PriorityCellComponent,
            },
            { field: DocumentNestedColumn.STATUS },
            {
                field: DocumentNestedColumn.AGE_IN_WF,
                headerName: 'Age In WF',
                cellRenderer: GrayBoxCellComponent,
                valueFormatter: getAgeInWFValue,
            },
            { field: DocumentNestedColumn.TAKEN },
            { field: DocumentNestedColumn.ANALYST },
            { field: DocumentNestedColumn.STATION },
            {
                field: DocumentNestedColumn.PRIMARY_FIXER,
                resizable: false,
            },
            ...customColumnsToColDefs(this.customColumns),
        ];

        // Set hide property instead of filtering out, so that already-open
        // detail grids can toggle visibility via setColumnsVisible().
        return columnDefs.map((col) => {
            const id = (col as any).colId ?? col.field;
            if (!id) return col;
            const shouldHide = this.hiddenNestedColumnOptions.has(
                String(id).toLowerCase(),
            );
            return shouldHide
                ? { ...col, hide: true }
                : { ...col, hide: false };
        });
    }

    // Deselect all discrepancies in expanded detail grids and clear indeterminate visuals
    private deselectAllDetailGridsAndIndeterminate() {
        if (!this._gridApi) return;
        this._gridApi.forEachNode((masterNode: IRowNode) => {
            if (!masterNode.data || !masterNode.master) return;
            const detailGridInfo: any = (masterNode as any).detailNode
                ?.detailGridInfo;
            if (detailGridInfo?.api) {
                detailGridInfo.api.forEachNode((child: IRowNode) => {
                    if (child.isSelected()) child.setSelected(false);
                });
            }
            this.clearMasterRowIndeterminate(masterNode);
        });
    }

    // Remove any lingering indeterminate classes from leaf discrepancy rows (should never be partial)
    private removeIndeterminateFromLeafRows() {
        // Target nested select column wrappers that accidentally carry partial style.
        const leafWrappers = document.querySelectorAll(
            '.ag-row [col-id="nested-select"] [ref="eWrapper"].ag-indeterminate',
        );
        leafWrappers.forEach((el) => el.classList.remove('ag-indeterminate'));
    }

    // Add indeterminate style for a master row's selection checkbox cell.
    private applyIndeterminateClass(rowIndex: number | null | undefined) {
        if (rowIndex === null || rowIndex === undefined) return;
        const selectors = [
            `.ag-pinned-left-cols-container .ag-row[row-index="${rowIndex}"] [col-id="select"] [ref="eWrapper"]`,
            `.ag-center-cols-container .ag-row[row-index="${rowIndex}"] [col-id="select"] [ref="eWrapper"]`,
        ];
        selectors.forEach((s) => {
            const el = document.querySelector(s);
            if (el) el.classList.add('ag-indeterminate');
        });
    }

    // Remove indeterminate style for a master row's selection checkbox cell.
    private removeIndeterminateClass(rowIndex: number | null | undefined) {
        if (rowIndex === null || rowIndex === undefined) return;
        const selectors = [
            `.ag-pinned-left-cols-container .ag-row[row-index="${rowIndex}"] [col-id="select"] [ref="eWrapper"]`,
            `.ag-center-cols-container .ag-row[row-index="${rowIndex}"] [col-id="select"] [ref="eWrapper"]`,
        ];
        selectors.forEach((s) => {
            const el = document.querySelector(s);
            if (el) el.classList.remove('ag-indeterminate');
        });
    }

    // Apply normal mode indeterminate (partial) visual across visible masters based on saved detail selections.
    private applyNormalIndeterminateFromSaved() {
        if (this.selectedAllAcrossPages || !this._gridApi) return;
        this._gridApi.forEachNode((node: IRowNode) => {
            if (!node?.master || !node?.data) return;
            const docId = node.data.id as number;
            const selectedData = this.detailSelections[docId];
            // If we have saved selections, compute partial using filtered count when available,
            // otherwise fall back to raw discrepanciesCount to avoid showing as fully selected.
            if (Array.isArray(selectedData) && selectedData.length > 0) {
                const selectedCount = selectedData.length;
                const hasFiltered = this.filteredDiscrepancyCounts.has(docId);
                const total = this.getFilteredCountForDoc(
                    docId,
                    (node.data?.discrepanciesCount ?? 0) as number,
                );
                // If filtered states are active but filtered total isn't ready, default to partial to avoid incorrect full.
                const defaultToPartial =
                    this.states?.length > 0 && !hasFiltered;
                const isPartial = defaultToPartial
                    ? true
                    : selectedCount > 0 && selectedCount < total;
                if (isPartial) this.applyIndeterminateClass(node.rowIndex);
                else this.removeIndeterminateClass(node.rowIndex);
            } else {
                // No saved selections: ensure master doesn't show partial.
                this.removeIndeterminateClass(node.rowIndex);
            }
        });
        this.removeIndeterminateFromLeafRows();
    }

    // Update partial visual for master in normal mode based on selected vs total detail counts.
    private updateNormalMasterIndeterminate(
        masterNode: IRowNode,
        _docId: number,
        selectedCount: number,
        totalCount: number,
    ) {
        const isPartial = selectedCount > 0 && selectedCount < totalCount;
        if (isPartial) this.markMasterRowIndeterminate(masterNode);
        else this.clearMasterRowIndeterminate(masterNode);
    }
}
